import type { ChatbotConfig, ChatbotCreateEvent } from "./schema";
import type {
  AiTrackingPredictionUserFeedbackEvent,
  PlatformInterfacesWebAnalyticsInterfaceConsumerInteractionEvent,
  RemoveAutoPopulatedEventFields,
} from "@zapier/avro-events";

export type ChatbotPromptProps = {
  accountId?: number;
  blockId?: string;
  config: ChatbotConfig;
  emitAiTrackingPredictionUserFeedbackEvent: (
    event: AiTrackingPredictionUserFeedbackEventPayload
  ) => void;
  emitConsumerInteractionEvent: (
    event: ConsumerInteractionEventPayload
  ) => void;
  fetcher: (
    input: RequestInfo | URL,
    init?: RequestInit | undefined
  ) => Promise<Response>;
  hideZapierButton: boolean;
  isDisabled?: boolean;
  isEditing: boolean;
  isEmbedded: boolean;
  isPopup?: boolean;
  noBackground?: boolean;
  onUsage: (event: OpenAiUsage) => void;
  pageId: string | undefined;
  projectId: string | undefined;
  renderPoweredByZapier: () => JSX.Element | null;
  triggerChatbotBlockAction: (
    event: ChatbotCreateEvent,
    notificationMessage?: string
  ) => Promise<void>;
};

type RemoveConstants<T> = Omit<
  RemoveAutoPopulatedEventFields<T>,
  | "system_name"
  | "customuser_id"
  | "account_id"
  | "timestamp_ms"
  | "visitor_id"
  | "session_id"
>;

export type ConsumerInteractionEventPayload =
  RemoveConstants<PlatformInterfacesWebAnalyticsInterfaceConsumerInteractionEvent.EventFields> & {
    embedded_flag?: boolean;
  };

export type AiTrackingPredictionUserFeedbackEventPayload =
  RemoveConstants<AiTrackingPredictionUserFeedbackEvent.EventFields>;

export type UserMsg = {
  content: string;
  role: "user";
  name?: string;
};

export type BotMsg = Record<string, unknown> & {
  id: string;
  name?: string;
} & (
    | {
        content: string;
        role: "assistant";
        error?: boolean;
        errorCode?: string;
        source?: string;
        function_call?: {
          name: string;
          arguments: string;
        };
      }
    | {
        content: string;
        role: "system";
        error?: never;
        errorCode?: never;
        source?: never;
      }
  );

export type FuncMsg = {
  name: string;
  content: string;
  role: "function";
};

export type Msg = UserMsg | BotMsg | FuncMsg;

export type OpenAIFnParamProps = {
  [key: string]: {
    type: string;
    description: string;
    enum?: string[];
  };
};

export type OpenAIFnMeta = {
  name: string;
  description: string;
  parameters: {
    type: "object";
    properties: OpenAIFnParamProps;
    required: string[];
  };
};

export function isBotMsg(msg: Msg): msg is BotMsg {
  return msg.role !== "user";
}

export type OpenAiUsage = {
  prompt_token_quantity: number;
  completion_token_quantity: number;
};

// OpenAI Functions have two parameters - one is the metadata we send to OpenAI
// to instruct it on how to call the function, and the other is the function itself.
// We're being fairly prescriptive that the function takes its arguments as keywords
// in a Record structure, and it must return a string in order to be readable by
// OpenAI chat completions (the string will generally be a stringified version of
// a JSON response object).
export type OpenAIFunctionImpl<
  T extends Record<string, any> = Record<string, any>
> = (args: T) => string | Promise<string>;

export type OpenAIFunctions = {
  [functionName: string]: {
    meta: OpenAIFnMeta;
    func: OpenAIFunctionImpl<any>;
  };
};

export type FunctionCall = {
  name: string;
  args: string;
};
